import type { ReactNode } from "react";

interface ConversationArtifactCardProps {
  eyebrow?: string;
  title: string;
  description?: string;
  descriptionTestId?: string;
  meta?: ReactNode;
  preview: ReactNode;
  actions?: ReactNode;
  status?: ReactNode;
  statusId?: string;
  testId?: string;
  className?: string;
}

/**
 * Shared conversation artifact frame for Bot and project Chat.
 *
 * Preview and decision controls deliberately live in one card. The frame owns
 * no mutation authority; callers supply only actions their current contract
 * can actually perform.
 */
export function ConversationArtifactCard({
  eyebrow,
  title,
  description,
  descriptionTestId,
  meta,
  preview,
  actions,
  status,
  statusId,
  testId,
  className = "",
}: ConversationArtifactCardProps) {
  return (
    <article
      data-testid={testId}
      className={`overflow-hidden rounded-lg border border-border bg-card shadow-sm ${className}`}
    >
      <header className="border-b border-border bg-muted/30 px-4 py-3">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {eyebrow ? (
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {eyebrow}
              </p>
            ) : null}
            <h3 className={`${eyebrow ? "mt-0.5 " : ""}text-sm font-semibold text-foreground`}>{title}</h3>
          </div>
          {meta ? <div className="shrink-0 text-[11px] text-muted-foreground">{meta}</div> : null}
        </div>
        {description ? (
          <p
            data-testid={descriptionTestId}
            className="mt-1.5 text-xs leading-5 text-muted-foreground"
          >
            {description}
          </p>
        ) : null}
      </header>
      <div className="p-3">{preview}</div>
      {actions || status ? (
        <footer className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/20 px-3 py-2.5">
          {actions}
          {status ? (
            <div
              id={statusId}
              role="status"
              aria-live="polite"
              aria-atomic="true"
              tabIndex={-1}
              className="min-w-0 flex-1 rounded-sm text-xs text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
            >
              {status}
            </div>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
}
